import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AMAZON_LINK = 'https://www.amazon.de';
const KEY_LOGO_URL = '/elemantra-key-logo.png';

export const Navigation = () => {
  const { language, toggleLanguage, t, countdown } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E5DED0]/50'
          : 'bg-transparent'
      }`}
      data-testid="navigation"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <motion.a
          href="#hero"
          onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}
          className="flex items-center gap-3 hover:opacity-70 transition-opacity duration-300"
          data-testid="logo"
        >
          <img 
            src={KEY_LOGO_URL} 
            alt="Elemantra" 
            className="h-8 md:h-10 w-auto object-contain"
          />
          <span className="font-cormorant text-lg md:text-xl tracking-[0.2em] text-[#2A2624] uppercase">
            Elemantra
          </span>
        </motion.a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
          <button
            onClick={() => scrollToSection('story')}
            className="text-xs tracking-[0.2em] uppercase text-[#5C5650] hover:text-[#2A2624] transition-colors duration-300"
            data-testid="nav-story"
          >
            {t.nav.story}
          </button>
          <button
            onClick={() => scrollToSection('benefits')}
            className="text-xs tracking-[0.2em] uppercase text-[#5C5650] hover:text-[#2A2624] transition-colors duration-300"
            data-testid="nav-benefits"
          >
            {t.nav.benefits}
          </button>
          <button
            onClick={() => scrollToSection('product')}
            className="text-xs tracking-[0.2em] uppercase text-[#5C5650] hover:text-[#2A2624] transition-colors duration-300"
            data-testid="nav-product"
          >
            {t.nav.product}
          </button>
          <button
            onClick={() => scrollToSection('why')}
            className="text-xs tracking-[0.2em] uppercase text-[#5C5650] hover:text-[#2A2624] transition-colors duration-300"
            data-testid="nav-why"
          >
            {t.nav.why}
          </button>
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="text-xs font-semibold tracking-widest text-[#5C5650] hover:text-[#B89C6A] transition-colors duration-300 ml-4"
            data-testid="lang-toggle"
          >
            {language === 'de' ? 'EN' : 'DE'}
          </button>

          {/* CTA Button */}
          <a
            href={countdown.amazonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#2A2624] text-[#FDFBF7] hover:bg-[#B89C6A] hover:text-[#2A2624] transition-colors duration-500 px-6 py-3 text-xs tracking-widest uppercase"
            data-testid="nav-buy-amazon"
          >
            {countdown.navText}
          </a>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
          <button
            onClick={toggleLanguage}
            className="text-xs font-semibold tracking-widest text-[#5C5650] hover:text-[#B89C6A] transition-colors duration-300"
            data-testid="lang-toggle-mobile"
          >
            {language === 'de' ? 'EN' : 'DE'}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-[#2A2624] p-2"
            data-testid="mobile-menu-btn"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {mobileMenuOpen ? (
                <path d="M6 6l12 12M6 18L18 6" />
              ) : (
                <path d="M4 8h16M4 16h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-[#FDFBF7] border-b border-[#E5DED0] px-6 py-8"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-6">
            <button
              onClick={() => scrollToSection('story')}
              className="text-sm tracking-[0.15em] uppercase text-[#5C5650] hover:text-[#2A2624] text-left"
            >
              {t.nav.story}
            </button>
            <button
              onClick={() => scrollToSection('benefits')}
              className="text-sm tracking-[0.15em] uppercase text-[#5C5650] hover:text-[#2A2624] text-left"
            >
              {t.nav.benefits}
            </button>
            <button
              onClick={() => scrollToSection('product')}
              className="text-sm tracking-[0.15em] uppercase text-[#5C5650] hover:text-[#2A2624] text-left"
            >
              {t.nav.product}
            </button>
            <button
              onClick={() => scrollToSection('why')}
              className="text-sm tracking-[0.15em] uppercase text-[#5C5650] hover:text-[#2A2624] text-left"
            >
              {t.nav.why}
            </button>
            <a
              href={countdown.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#2A2624] text-[#FDFBF7] hover:bg-[#B89C6A] hover:text-[#2A2624] transition-colors duration-500 px-6 py-3 text-xs tracking-widest uppercase text-center mt-4"
            >
              {countdown.navText}
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
